import React, { useEffect, useState } from "react";
import { Document, Image, Page, PDFViewer, Text, View } from "@react-pdf/renderer";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { getJrDetail, getJrLineDetail, clearDetail, jrV2Selector } from "./jrV2Slice";
import { s, SHRow } from "../output/CetakJR";
import { Colors } from "../../../assets/Colors";
import { GS_FORM, calcBilled } from "../output/GeneralServiceEntryEditor";
import { cfgForLine, calcBilledPR, fmtPeriode } from "../output/PortRentalEntryEditor";

// Cetak Job Request V2 (JrHeader/JrLine).
//
// Kop surat, footer, kotak Document No., sub-header dan seluruh gaya diambil
// LANGSUNG dari CetakJR (`s`, `SHRow`) — bukan disalin — supaya tampilannya
// dijamin sama dan tidak melenceng saat CetakJR diubah.
//
// Bedanya hanya pada DETAIL ACTIVITY. CetakJR menggambar tabel lewat
// `objectDrawer`, yaitu skema tabel PER JENIS ITEM; dari 23 ItemName yang
// dipakai JR V2 hanya 4 yang punya skema di sana, sehingga memakainya akan
// menghasilkan tabel kosong untuk mayoritas item. Karena itu di sini dipakai
// satu tabel generik yang berlaku untuk semua item.
//
// Nama item diambil dari "ItemDisplay" (sudah berupa nama layak baca, mis.
// AR0001 → "Warehouse Covered"); "ItemName" hanya cadangan karena sebagian
// baris menyimpan kode mentah, dan ada baris yang keduanya kosong.

const fmtQty = (n) => {
  const v = Number(n ?? 0);
  if (!isFinite(v)) return "-";
  return new Intl.NumberFormat("id-ID", { minimumFractionDigits: 4, maximumFractionDigits: 4 }).format(v);
};

const fmtDate = (val) => {
  if (!val) return "-";
  const d = new Date(val);
  if (isNaN(d.getTime())) return String(val);
  return d.toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" });
};

const namaItem = (l) =>
  l.ItemDisplay || l.ItemName || (l.ActivityName || "").replace(/_/g, " ") || "-";

// Kolom tabel generik. Lebar dijumlahkan 100%.
const KOLOM = [
  { w: "34%", head: "Layanan" },
  { w: "13%", head: "Qty" },
  { w: "13%", head: "Satuan" },
  { w: "15%", head: "Status" },
  // Isinya: alasan tolak, lalu catatan baris, lalu rangkuman keterangan ENTRI
  // harian (EntryRemarks) — yang terakhir itulah yang selama ini diketik pengguna
  // di form Activity tapi tak pernah ikut tercetak.
  { w: "25%", head: "Keterangan" },
];

// ── Entri harian per baris ──────────────────────────────────────────────────
// Skema kolomnya SAMA dengan yang dipakai halaman detail on-screen
// (GeneralServiceSummary/PortRentalSummary) — satu sumber kebenaran, bukan
// disalin ulang, supaya cetak tak pernah lebih tipis dari yang terlihat di layar.
const cfgFor = (l) =>
  l.ActivityName === "GENERAL_SERVICE" ? (GS_FORM[l.SubType] || GS_FORM.OTHERS) : cfgForLine(l);
const calcFor = (l, formula, e) =>
  l.ActivityName === "GENERAL_SERVICE" ? calcBilled(formula, e) : calcBilledPR(formula, e);

// "datetime-local" tersimpan tanpa zona waktu — dibaca apa adanya, bukan lewat
// Date (yang bisa menggeser jam sesuai zona), lih. catatan sama di PortRentalSummary.jsx.
const fmtDT = (v) => {
  const m = String(v || "").match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}:\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]} ${m[4]}` : (v || "-");
};

// Sub-tabel entri harian — baris per baris SAMA JUMLAHNYA dengan entri di
// halaman Activity (satu entri = satu baris, persis seperti editornya), bukan
// dirangkum jadi paragraf. Yang dulu bikin ini berantakan bukan bentuk
// tabelnya, tapi lebar kolom dibagi SAMA RATA — field teks bebas (nomor unit,
// deskripsi material) jauh lebih panjang dari field tanggal/qty, jadi lebar
// kolom di sini dibobot per TIPE kolom, bukan dibagi rata. Nilai yang tetap
// panjang akan membungkus ke baris berikutnya DI DALAM sel itu (menambah
// tinggi baris), bukan meluber keluar kolom.
const BOBOT_KOLOM = {
  label: 1.4, text: 2.4, roomselect: 1.2, select: 1,
  date: 1.3, month: 1.3, "datetime-local": 1.5, uomSelect: 0.8,
  number: 0.8, uom: 0.7, check: 0.7, link: 0.9,
};
function lebarKolomEntri(cols) {
  const bobot = cols.map(c => BOBOT_KOLOM[c.type] ?? 1);
  const totalBobot = bobot.reduce((a, b) => a + b, 0) + 0.9; // +0.9 milik kolom Jumlah
  return bobot.map(b => `${((b / totalBobot) * 100).toFixed(2)}%`);
}

const sEntry = {
  wrap:     { marginBottom: 4, borderWidth: 0.3, borderColor: "#D1D5DB" },
  head:     { flexDirection: "row", backgroundColor: "#F3F4F6" },
  cellHead: { fontSize: 5.5, fontWeight: 700, color: "#4B5563", padding: 2 },
  row:      { flexDirection: "row", borderTopWidth: 0.3, borderTopColor: "#E5E7EB" },
  cell:     { fontSize: 6, color: "#374151", padding: 2 },
  subtotalRow:   { flexDirection: "row", justifyContent: "space-between",
                   backgroundColor: "#F3F4F6", borderTopWidth: 0.5, borderTopColor: "#D1D5DB",
                   paddingVertical: 3, paddingHorizontal: 4 },
  subtotalLabel: { fontSize: 6.5, color: "#6B7280" },
  subtotalVal:   { fontSize: 6.5, fontWeight: 700, color: "#374151" },
};

// PDF tak bisa menampilkan tautan/checkbox interaktif seperti versi layar —
// keduanya diringkas jadi teks datar (Ya/Tidak, Ada/-).
function renderEntryVal(col, e, uom, itemLabel) {
  switch (col.type) {
    case "label":          return itemLabel;
    case "uom":            return uom;
    case "check":          return e.HouseKeeping ? "Ya" : "-";
    case "roomselect":     return e.RoomRef || "-";
    case "link":           return e.EvidenceUrl ? "Ada" : "-";
    case "number":         return e[col.key] === "" || e[col.key] == null ? "-" : fmtQty(e[col.key]);
    case "datetime-local": return fmtDT(e[col.key]);
    case "month":          return fmtPeriode(e[col.key]);
    default:               return e[col.key] || "-"; // text/select/date/uomSelect
  }
}

const LABEL_STATUS = {
  approved: "Disetujui",
  rejected: "Ditolak",
  pending: "Menunggu",
};

const JrV2Document = ({ data, lineEntries = {} }) => {
  if (!data) return null;

  const lines = data.Lines ?? [];

  // Kelompokkan per aktivitas, urutan mengikuti kemunculan pertama (SortOrder
  // sudah diterapkan backend lewat ORDER BY, jadi jangan diurut ulang).
  const grup = [];
  const indeks = new Map();
  lines.forEach((l) => {
    const key = l.ActivityName || "LAINNYA";
    if (!indeks.has(key)) {
      indeks.set(key, grup.length);
      grup.push({ nama: key, items: [] });
    }
    grup[indeks.get(key)].items.push(l);
  });

  return (
    <Document>
      <Page size="A4" style={s.page} wrap>

        {/* ── FOOTER (tetap di tiap halaman) ── */}
        <View style={s.pageFooter} fixed>
          <Text style={s.footText}>
            PT. RANJI KARYA SAKTI | Pendingin, Sanga Sanga, Kutai Kartanegara, East Kalimantan 75267
          </Text>
          <Text
            style={s.footText}
            render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`}
          />
        </View>

        {/* ── HEADER ── */}
        <View style={s.headerWrap}>
          <View style={s.headerLeft}>
            <Text style={s.companyName}>PT. RANJI KARYA SAKTI</Text>
            <Text style={s.companyAddr}>
              Pendingin, Sanga Sanga, Kutai Kartanegara{"\n"}
              East Kalimantan 75267{"\n"}
              Telp: (021) 22909819 | info@ranjikaryasakti.co.id
            </Text>
          </View>

          <View style={s.headerCenter}>
            <View style={s.titleBox}>
              <Text style={s.titleText}>JOB REQUEST</Text>
            </View>
          </View>

          <View style={s.headerRight}>
            <View style={s.docInfoTable}>
              <View style={s.docInfoRow}>
                <Text style={s.docInfoLabel}>Document No.</Text>
                <Text style={s.docInfoVal}>RKS-OPS-FO-001</Text>
              </View>
              <View style={s.docInfoRow}>
                <Text style={s.docInfoLabel}>Issued Date</Text>
                <Text style={s.docInfoVal}>12-08-2018</Text>
              </View>
              <View style={s.docInfoRow}>
                <Text style={s.docInfoLabel}>Revision</Text>
                <Text style={s.docInfoVal}>0</Text>
              </View>
              <View style={s.docInfoRowLast}>
                <Text style={s.docInfoLabel}>Page</Text>
                <Text
                  style={s.docInfoVal}
                  render={({ pageNumber, totalPages }) => `${pageNumber} of ${totalPages}`}
                />
              </View>
            </View>
          </View>
        </View>

        {/* ── SUB-HEADER ── */}
        <View style={s.subHeaderBox}>
          <SHRow label="Contract No"  value={data.NoKontrak || data.QoNo} />
          <SHRow label="Nomor JR"     value={data.JrID} />
          <SHRow label="SPK No"       value={data.SPKNo} />
          <SHRow label="WSB No"       value={data.WSBNo} />
          <SHRow label="Priority"     value={data.Priority} />
          <SHRow label="Request Unit" value={data.RequestUnit} />
          <SHRow label="Tgl Pekerjaan" value={fmtDate(data.WorkDate)} />

          <Text style={s.subHeaderGreeting}>
            Kepada / To: {"PT. Ranji Karya Sakti (Kutai Logistic Base)"}
            {data.NamaKlien
              ? `\nBersama ini kami mengajukan Job Request kepada Bapak/Ibu ${data.NamaKlien}`
              : ""}
          </Text>

          {/* Checklist aktivitas — semua yang muncul di JR ini dianggap terpilih */}
          {grup.length > 0 && (
            <View style={s.checklistWrap}>
              {grup.map((g, i) => (
                <View key={i} style={s.checklistItem}>
                  <Text style={s.checkBoxChecked}>T</Text>
                  <Text style={s.checkLabel}>{g.nama.replace(/_/g, " ")}</Text>
                </View>
              ))}
            </View>
          )}
        </View>

        {/* ── DETAIL ACTIVITY ── */}
        {grup.length > 0 && (
          <View>
            <Text style={s.secTitle}>DETAIL ACTIVITY</Text>

            {grup.map((g, gi) => (
              <View key={gi}>
                <Text style={[s.subTitle, { backgroundColor: "#374151" }]}>
                  {g.nama.replace(/_/g, " ")}
                </Text>

                <View style={s.table}>
                  <View style={s.tRowHead}>
                    {KOLOM.map((k, i) => (
                      <Text key={i} style={[s.tCellHead, { width: k.w }]}>{k.head}</Text>
                    ))}
                  </View>

                  {g.items.map((l, i) => {
                    const entries = lineEntries[l.JrLineID] ?? [];
                    const cfg = cfgFor(l);
                    const uom = l.SatuanNama || l.SatuanID || "-";
                    const itemLabel = namaItem(l);
                    const wCols = lebarKolomEntri(cfg.cols);
                    const wJumlah = `${(0.9 / (cfg.cols.reduce((a, c) => a + (BOBOT_KOLOM[c.type] ?? 1), 0) + 0.9) * 100).toFixed(2)}%`;
                    const subtotal = entries.reduce((sum, e) => sum + calcFor(l, cfg.formula, e), 0);
                    return (
                      <View key={i}>
                        <View style={i % 2 === 0 ? s.tRow : s.tRowAlt}>
                          {[
                            itemLabel,
                            fmtQty(l.ActualQty),
                            uom,
                            LABEL_STATUS[l.ItemStatus] || l.ItemStatus || "-",
                            l.RejectReason || l.Remarks || l.EntryRemarks || "-",
                          ].map((v, ci) => (
                            <Text key={ci} style={[s.tCell, { width: KOLOM[ci].w }]}>{v}</Text>
                          ))}
                        </View>
                        {/* Sub-tabel entri harian: satu baris per entri, JUMLAH
                            BARISNYA SAMA dengan yang diisi di halaman Activity.
                            Lebar kolom dibobot per tipe (field teks bebas dapat
                            porsi lebih besar dari qty/tanggal) — nilai yang tetap
                            panjang membungkus DI DALAM selnya sendiri (baris jadi
                            lebih tinggi), bukan meluber ke kolom sebelah. */}
                        {entries.length > 0 && (
                          <View style={sEntry.wrap}>
                            <View style={sEntry.head}>
                              {cfg.cols.map((c, ci) => (
                                <Text key={ci} style={[sEntry.cellHead, { width: wCols[ci] }]}>{c.label}</Text>
                              ))}
                              <Text style={[sEntry.cellHead, { width: wJumlah, textAlign: "right" }]}>Jumlah</Text>
                            </View>
                            {entries.map((e, ei) => (
                              <View key={ei} style={sEntry.row}>
                                {cfg.cols.map((c, ci) => (
                                  <Text key={ci} style={[sEntry.cell, { width: wCols[ci] }]}>
                                    {renderEntryVal(c, e, uom, itemLabel)}
                                  </Text>
                                ))}
                                <Text style={[sEntry.cell, { width: wJumlah, textAlign: "right", fontWeight: 700 }]}>
                                  {fmtQty(calcFor(l, cfg.formula, e))}
                                </Text>
                              </View>
                            ))}
                            <View style={sEntry.subtotalRow}>
                              <Text style={sEntry.subtotalLabel}>Subtotal</Text>
                              <Text style={sEntry.subtotalVal}>{fmtQty(subtotal)} {uom}</Text>
                            </View>
                          </View>
                        )}
                      </View>
                    );
                  })}
                </View>
              </View>
            ))}
          </View>
        )}

        {data.Remarks ? (
          <View>
            <Text style={s.secTitle}>KETERANGAN</Text>
            <Text style={s.tCell}>{data.Remarks}</Text>
          </View>
        ) : null}

        {/* ── TANDA TANGAN — Klien kiri, Manager Operational kanan ── */}
        <View style={s.signWrap}>
          <View style={s.signCol}>
            <Text style={[s.signRole, { marginBottom: 4 }]}>Klien</Text>
            <View style={{ height: 60 }} />
            <View style={s.signLine} />
            <Text style={s.signName}>{data.NamaKlien || "-"}</Text>
          </View>
          <View style={s.signCol}>
            <Text style={[s.signRole, { marginBottom: 4 }]}>Manager Operational</Text>
            <Image style={{ height: 60, alignSelf: "center" }}
              src="/speciment/FatchulChaqqi.png" />
            <View style={s.signLine} />
            <Text style={s.signName}>Fatchul Chaqqi</Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};

const CetakJrV2 = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const jrV2 = useSelector(jrV2Selector);
  const jrID = location.state?.JrID ?? null;

  const [lineEntries, setLineEntries] = useState({});
  const [entriesLoading, setEntriesLoading] = useState(true);

  useEffect(() => {
    if (jrID) dispatch(getJrDetail(jrID));
    return () => { dispatch(clearDetail()); };
  }, [jrID]); // eslint-disable-line

  // Cetak harus SELENGKAP layar: begitu header + baris JR selesai dimuat,
  // tarik entri harian tiap baris sekaligus (bukan lazy per-klik seperti
  // GeneralServiceSummary/PortRentalSummary — di cetak tak ada "klik" sama sekali).
  useEffect(() => {
    const lines = jrV2.detail?.Lines ?? [];
    if (!lines.length) { setEntriesLoading(false); return; }
    setEntriesLoading(true);
    Promise.all(lines.map(l =>
      dispatch(getJrLineDetail(l.JrLineID)).unwrap()
        .then(res => [l.JrLineID, (res?.data ?? []).map(d => ({ ...d, ...(d.Attributes || {}) }))])
        .catch(() => [l.JrLineID, []])
    )).then(pairs => {
      setLineEntries(Object.fromEntries(pairs));
      setEntriesLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jrV2.detail?.JrID]);

  const siap = jrID && !jrV2.isDetailLoading && jrV2.detail && !entriesLoading;

  return (
    <div className="md:ml-64">
      <div style={{ background: Colors.primary }} className="pt-14 pb-16 px-3 md:px-8 h-auto">
        <div className="container mx-auto max-w-full">
          <div className="grid grid-cols-1" />
        </div>
      </div>

      <div className="px-3 md:px-8 h-screen -mt-32">
        {!jrID && (
          <div className="flex justify-center items-center h-full text-gray-400">
            JR tidak ditemukan — buka dari daftar Job Request.
          </div>
        )}

        {jrID && !siap && (
          <div className="flex justify-center items-center h-full">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto" />
              <p className="mt-4 text-gray-600">Memuat dokumen…</p>
            </div>
          </div>
        )}

        {siap && (
          <div className="h-screen w-full overflow-hidden">
            <PDFViewer
              className="h-screen w-full"
              style={{ width: "100%", height: "100%", minWidth: "100%", minHeight: "100%" }}
            >
              <JrV2Document data={jrV2.detail} lineEntries={lineEntries} />
            </PDFViewer>
          </div>
        )}
      </div>
    </div>
  );
};

export default CetakJrV2;
